import AOS from 'aos';

const ERROR_MESSAGES = {
	INIT_FAILED: 'AOS: Initialization failed',
};

/**
 * Initialize AOS (Animate On Scroll) library
 * @returns {void}
 */
export const initializeAOS = () => {
	try {
		AOS.init({
			// Centralized config
			disable: 'tablet',
			startEvent: 'DOMContentLoaded',
			initClassName: 'aos-init',
			animatedClassName: 'aos-animate',
			useClassNames: false,
			disableMutationObserver: false,
			debounceDelay: 10,
			throttleDelay: 50,
			offset: 100,
			duration: 500,
			easing: 'ease',
			once: true,
			mirror: false,
			anchorPlacement: 'top-bottom',
		});

		// Add additional scroll event handling for fast scrolling
		let scrollTimeout;
		let isScrolling = false;

		const handleScroll = () => {
			isScrolling = true;
			
			// Clear existing timeout
			if (scrollTimeout) {
				clearTimeout(scrollTimeout);
			}

			// Set a timeout to refresh AOS after scrolling stops
			scrollTimeout = setTimeout(() => {
				isScrolling = false;
				// Refresh AOS to catch any missed animations
				if (typeof AOS !== 'undefined') {
					AOS.refresh();
				}
			}, 150); // Wait 150ms after scrolling stops
		};

		// Use passive scroll listener for better performance
		window.addEventListener('scroll', handleScroll, { passive: true });

		// Also refresh AOS on window resize
		window.addEventListener('resize', () => {
			if (typeof AOS !== 'undefined') {
				AOS.refresh();
			}
		}, { passive: true });

		// Refresh AOS after a short delay to ensure all elements are properly initialized
		setTimeout(() => {
			if (typeof AOS !== 'undefined') {
				AOS.refresh();
			}
		}, 200);

		// Add a periodic check for missed animations during fast scrolling
		let missedAnimationCheck;
		const checkForMissedAnimations = () => {
			// Look for elements that should be animated but aren't
			const aosElements = document.querySelectorAll('[data-aos]');
			const missedElements = Array.from(aosElements).filter(el => {
				const rect = el.getBoundingClientRect();
				const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
				const hasAosInit = el.classList.contains('aos-init');
				const hasAosAnimate = el.classList.contains('aos-animate');
				
				// Element is in viewport but hasn't been animated
				return isInViewport && hasAosInit && !hasAosAnimate;
			});

			if (missedElements.length > 0) {
				// Refresh AOS to trigger missed animations
				if (typeof AOS !== 'undefined') {
					AOS.refresh();
				}
			}
		};

		// Check for missed animations every 2 seconds
		missedAnimationCheck = setInterval(checkForMissedAnimations, 2000);

		// Clean up interval when page unloads
		window.addEventListener('beforeunload', () => {
			if (missedAnimationCheck) {
				clearInterval(missedAnimationCheck);
			}
		});

		// Add keyboard shortcut for manual AOS refresh (Ctrl+Shift+A)
		document.addEventListener('keydown', (event) => {
			if (event.ctrlKey && event.shiftKey && event.key === 'A') {
				event.preventDefault();
				forceCheckAOS();
			}
		});

	} catch (error) {
		console.error(ERROR_MESSAGES.INIT_FAILED, error);
	}
};

/**
 * Refresh AOS with additional error handling and performance optimization
 * @returns {void}
 */
export const refreshAOS = () => {
	try {
		if (typeof AOS !== 'undefined') {
			// Use requestAnimationFrame for better performance
			requestAnimationFrame(() => {
				AOS.refresh();
			});
		}
	} catch (error) {
		console.error('AOS: Refresh failed', error);
	}
};

/**
 * Force check for and trigger any missed AOS animations
 * @returns {void}
 */
export const forceCheckAOS = () => {
	try {
		if (typeof AOS !== 'undefined') {
			// Force refresh AOS
			AOS.refresh();
			
			// Manually check for elements that should be animated
			const aosElements = document.querySelectorAll('[data-aos]');
			aosElements.forEach(el => {
				const rect = el.getBoundingClientRect();
				const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
				
				if (isInViewport && !el.classList.contains('aos-animate')) {
					// Force the animation to trigger
					el.classList.add('aos-animate');
				}
			});
		}
	} catch (error) {
		console.error('AOS: Force check failed', error);
	}
};
